import { useMaterialCalculations } from '@/hooks/useMaterialCalculations'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

/**
 * Service filter mapping for each section (matches what the section panels use).
 */
const SECTION_SERVICE_FILTER: Record<string, string> = {
  asphalt: 'Asphalt',
  'asphalt-rnr': 'Asphalt',
  'asphalt-overlay': 'Asphalt',
  concrete: 'Concrete',
  grading: 'Grading',
  'seal-coat': 'Seal Coating',
}

interface SectionSyncProps {
  section: string
  serviceFilter?: string
  scopeData: string | undefined
  pricingItems: PricingItem[]
  lineItems: LineItem[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

/**
 * Invisible component that runs useMaterialCalculations for a single section.
 * This ensures writeup-driven material line items are created even when
 * the user hasn't visited that section's tab.
 */
function SectionSync({ section, serviceFilter, scopeData, pricingItems, lineItems, onAddItem, onUpdateItem, onDeleteItem }: SectionSyncProps) {
  useMaterialCalculations({
    scopeData,
    section,
    serviceFilter,
    pricingItems,
    lineItems,
    onAddItem,
    onUpdateItem,
    onDeleteItem,
  })
  return null
}

interface BackgroundMaterialSyncProps {
  /** Set of added category section IDs (e.g., 'asphalt', 'concrete') */
  sections: string[]
  scopeData: string | undefined
  pricingItems: PricingItem[]
  lineItems: LineItem[]
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

/**
 * Mounts invisible SectionSync components for ALL added sections.
 * This is the single source of sync for formula-driven materials —
 * MaterialsSection only displays items, it does not run its own sync.
 * Keeping SectionSync mounted regardless of active tab means there is
 * never more than one sync running per section at a time, eliminating
 * the race condition that caused materials to load slowly or go missing
 * when switching tabs.
 */
export function BackgroundMaterialSync({
  sections,
  scopeData,
  pricingItems,
  lineItems,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
}: BackgroundMaterialSyncProps) {
  const effectiveSections = sections.flatMap((s) =>
    s === 'asphalt' ? [s, 'asphalt-rnr', 'asphalt-overlay'] : [s]
  )

  return (
    <>
      {effectiveSections.map((section) => (
        <SectionSync
          key={section}
          section={section}
          serviceFilter={SECTION_SERVICE_FILTER[section]}
          scopeData={scopeData}
          pricingItems={pricingItems}
          lineItems={lineItems}
          onAddItem={onAddItem}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
        />
      ))}
    </>
  )
}
